import type { ManagementAccountFieldName, ManagementAccountFieldPermissions } from '@common/types/user';
import { Roles } from '@common/types/user';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
    alpha,
    Box,
    BoxProps,
    debounce,
    FormHelperText,
    IconButton,
    InputAdornment,
    Skeleton,
    TextField,
    TextFieldProps,
    Typography,
} from '@mui/material';
import { isFieldRequiredByPermission } from '@utils/fieldPermissions';
import clsx from 'clsx';
import { useField, useFormikContext } from 'formik';
import React, { FocusEvent, forwardRef, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

// height of the TextField
const DEFAULT_HEIGHT = 44;

// magic number which must be set appropriately for height
const labelOffset = -2;

const labelNoValueOffset = -6;

type BaseProps = TextFieldProps & {
    height?: number;
    buffered?: boolean;
    topLabel?: React.ReactNode;
    boxProps?: Omit<BoxProps, 'children'>;
    loading?: boolean;
};

type BufferedTextFieldProps = Omit<BaseProps, 'name' | 'value' | 'onChange' | 'buffered' | 'error'> & {
    name: string;
    onChange?: TextFieldProps['onChange'];
    debounceMs?: number;
    validateOnCommit?: boolean;
    shouldChange?: (e: React.ChangeEvent<HTMLInputElement>) => boolean;
    disableOnSubmitting?: boolean;
    noDebounce?: boolean;
};

// Discriminated union: buffered true vs false/undefined
type UnbufferedProps = BaseProps & { buffered?: false };
type BufferedProps = BufferedTextFieldProps & { buffered: true };
type InputFieldProps = UnbufferedProps | BufferedProps;

const RawInputField = forwardRef<HTMLDivElement, BaseProps>(
    ({ height = DEFAULT_HEIGHT, topLabel, boxProps, loading, ...props }, ref) => {
        const [isFocused, setIsFocused] = useState(false);

        const handleFocus = useCallback(
            (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                setIsFocused(true);
                props.onFocus?.(e);
            },
            [props]
        );

        const handleBlur = useCallback(
            (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                setIsFocused(false);
                props.onBlur?.(e);
            },
            [props]
        );

        const hasValue = useMemo(() => !!props.value, [props.value]);

        const top = useMemo(() => {
            if (hasValue || isFocused) {
                return labelOffset;
            }
            return labelNoValueOffset;
        }, [hasValue, isFocused]);

        const textField = (
            <TextField
                ref={ref}
                {...props}
                onFocus={handleFocus}
                onBlur={handleBlur}
                helperText={loading ? undefined : props.helperText}
                slotProps={{
                    input: {
                        ...props.InputProps,
                        ...props.slotProps?.input,
                        className: clsx((props.slotProps?.input as any)?.className, 'rounded-lg font-inter', {
                            shadow: !props.disabled,
                        }),
                    },
                    htmlInput: {
                        ...props.inputProps,
                        ...props.slotProps?.htmlInput,
                        style: {
                            padding: !props.multiline ? '0 14px' : undefined,
                            ...(props.slotProps?.htmlInput as any)?.style,
                            height: !props.multiline ? height : undefined,
                        },
                    },
                    inputLabel: {
                        ...props.slotProps?.inputLabel,
                        sx: {
                            ...(props.slotProps?.inputLabel as any)?.sx,
                            color: (theme) => theme.palette['neutral-900'].main,
                            top,
                        },
                    },
                }}
                sx={{
                    ...props.sx,
                    '& ::placeholder': {
                        color: (theme) => `${alpha(theme.palette['neutral-900'].main, 0.5)} !important`,
                        opacity: 1,
                    },
                }}
            />
        );

        const loadingWrapper = loading ? (
            <Box>
                <Skeleton className="max-w-none" variant="rounded">
                    {textField}
                </Skeleton>
                {props.helperText ? (
                    <FormHelperText error={props.error} sx={{ mt: '3px' }} disabled={loading}>
                        {props.helperText}
                    </FormHelperText>
                ) : null}
            </Box>
        ) : (
            textField
        );

        if (!topLabel) {
            return loadingWrapper;
        }

        return (
            <Box {...boxProps}>
                <Typography fontFamily="museo-sans" fontSize={16} fontWeight={600} mb="10px" color="neutral-900">
                    {topLabel}
                    {props.required ? (
                        <Typography component="span" color="signal-warning">
                            {' '}
                            *
                        </Typography>
                    ) : null}
                </Typography>
                {loadingWrapper}
            </Box>
        );
    }
);
RawInputField.displayName = 'RawInputField';

type FormValuesWithFieldPermissions = {
    field_permissions?: Partial<Record<string, ManagementAccountFieldPermissions[ManagementAccountFieldName]>> | null;
    role?: Roles;
    job_title?: string;
};

const doesFormHasFieldPermissions = (values: unknown): values is FormValuesWithFieldPermissions => {
    return typeof values === 'object' && values !== null && 'field_permissions' in values;
};

const doesFormHasEditFlag = (values: unknown): values is { edit: boolean } => {
    return typeof values === 'object' && values !== null && 'edit' in values;
};

const doesFormStatusHaveEditFlag = (status: unknown): status is { edit: boolean } => {
    return typeof status === 'object' && status !== null && 'edit' in status;
};

const BufferedInputField = forwardRef<HTMLDivElement, BufferedTextFieldProps>(
    (
        {
            name,
            debounceMs = 250,
            validateOnCommit = true,
            onBlur,
            shouldChange = () => true,
            disableOnSubmitting = true,
            noDebounce = false,
            onChange,
            ...rest
        },
        ref
    ) => {
        const [field, meta, helpers] = useField<string>(name);
        const { validateField, isSubmitting, values, status } = useFormikContext();
        const [localValue, setLocalValue] = useState(field.value ?? '');
        const lastCommitted = useRef(field.value ?? '');
        const isLoading = rest.loading || (disableOnSubmitting && isSubmitting);

        const hasFieldPermissions = doesFormHasFieldPermissions(values);
        const fieldPermission = hasFieldPermissions ? values.field_permissions?.[name] : undefined;
        const editFlag = doesFormHasEditFlag(values)
            ? values.edit
            : doesFormStatusHaveEditFlag(status)
            ? status.edit
            : undefined;
        const isReadOnly = editFlag === false || fieldPermission?.read_only === true;

        const isRequired = isFieldRequiredByPermission({
            permission: fieldPermission,
            role: hasFieldPermissions ? values.role : undefined,
            jobTitle: hasFieldPermissions ? values.job_title : undefined,
        });

        // Sync external (e.g. reinitialize) changes
        useEffect(() => {
            if (field.value !== lastCommitted.current) {
                setLocalValue(field.value ?? '');
                lastCommitted.current = field.value ?? '';
            }
        }, [field.value]);

        const commit = useMemo(
            () =>
                noDebounce
                    ? (val: string) => {
                          lastCommitted.current = val;
                          // Skip validation during typing
                          helpers.setValue(val, validateOnCommit);
                      }
                    : debounce((val: string) => {
                          lastCommitted.current = val;
                          // Skip validation during typing
                          helpers.setValue(val, validateOnCommit);
                      }, debounceMs),
            [helpers, debounceMs, validateOnCommit, noDebounce]
        );

        const handleChange = useCallback(
            (e: React.ChangeEvent<HTMLInputElement>) => {
                if (!shouldChange(e)) {
                    return;
                }
                const val = e.target.value;
                setLocalValue(val);
                commit(val);
                onChange?.(e);
            },
            [commit, onChange, shouldChange]
        );

        const handleBlur = useCallback(
            (e: React.FocusEvent<HTMLInputElement>) => {
                commit(e.target.value);
                helpers.setTouched(true, false);
                // Final validation on blur
                validateField(name);
                onBlur?.(e);
            },
            [commit, helpers, name, onBlur, validateField]
        );

        return (
            <RawInputField
                ref={ref}
                {...rest}
                name={name}
                value={localValue}
                onChange={handleChange}
                onBlur={handleBlur}
                error={meta.touched && Boolean(meta.error)}
                helperText={meta.touched && meta.error ? meta.error : rest.helperText}
                loading={isLoading}
                disabled={rest.disabled || (disableOnSubmitting && isSubmitting) || isReadOnly}
                required={isRequired || rest.required}
            />
        );
    }
);
BufferedInputField.displayName = 'BufferedInputField';

const InputField = memo(
    forwardRef<HTMLDivElement, InputFieldProps>((props, ref) => {
        const { buffered, ...rest } = props;
        if (buffered) {
            return <BufferedInputField ref={ref} {...props} />;
        }
        return <RawInputField ref={ref} {...rest} />;
    })
);
InputField.displayName = 'InputField';

const InputFieldPassword = memo(
    forwardRef<HTMLDivElement, InputFieldProps>((props, ref) => {
        const [showPassword, setShowPassword] = useState(false);

        return (
            <InputField
                ref={ref}
                {...props}
                type={showPassword ? 'text' : 'password'}
                slotProps={{
                    ...props.slotProps,
                    input: {
                        ...props.slotProps?.input,
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    tabIndex={-1}
                                    aria-label="toggle password visibility"
                                    onClick={() => setShowPassword(!showPassword)}
                                    onMouseDown={(e) => e.preventDefault()}
                                    edge="end"
                                >
                                    {showPassword ? (
                                        <VisibilityOffOutlinedIcon color="black" />
                                    ) : (
                                        <VisibilityOutlinedIcon color="black" />
                                    )}
                                </IconButton>
                            </InputAdornment>
                        ),
                    },
                }}
            />
        );
    })
);
InputFieldPassword.displayName = 'InputFieldPassword';

const InputFieldNamespace = Object.assign(InputField, {
    Default: InputField,
    Password: InputFieldPassword,
});

export { InputFieldNamespace as InputField };
export type { InputFieldProps };
